import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  Box,
  Button,
  DND_DRAGGABLE_PROPS,
  Icon,
  Input,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Skeleton,
  sortableHandleProps,
  sortableStyle,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  VisualPicker,
  useMenuFocusStealGuard,
  type Omittable,
} from "@salestrak/ui";
import {
  type StageType,
} from "#app/lib/data";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { defaultMarkerHint, EntityTag } from "#app/components/entity";
import {
  deleteBlocker,
  type DraftActions,
  type DraftGroups,
  type StageDraft,
} from "./use-stage-drafts";


// Mirrors the server's type-driven percentage (stages.service.ts) for the
// draft chips — a drafted stage has no server icon to read yet.
const STAGE_TYPE_PERCENTAGE: Record<StageType, number> = {
  pending: 0,
  inProgress: 50,
  done: 100,
  canceled: 0,
};

// ---------------------------------------------------------------------------
// Group section — quiet header, stacked pill rows, dashed add row
// ---------------------------------------------------------------------------

// The stage list column (the ClickUp-statuses shape): quiet category labels
// over free-standing bordered rows, kept narrow so the pills read as rows,
// not bars.
export const STAGE_LIST_MAX_WIDTH = 480;

// What each category MEANS — a label alone is a heading with no rule behind
// it, so the header carries a help glyph spelling out the semantics.
const STAGE_TYPE_HELP: Record<StageType, string> = {
  pending: "Work that hasn't started yet.",
  inProgress: "Work that's actively underway.",
  done: "Work that finished successfully.",
  canceled: "Work that stopped and won't be finished.",
};

function GroupHeader({
  type,
  onAddStage,
}: {
  type: StageType;
  onAddStage?: ((type: StageType) => void) | undefined;
}) {
  const label = stageTypeLabel(type);
  return (
    // paddingRight mirrors the pills' inner inset so the header's ＋ sits on
    // the same column as the rows' ⋯ actions.
    <Box direction="row" align="center" justify="between" minHeight={24} paddingRight="xs">
      <Box direction="row" align="center" gap="xs">
        <Text size="xs" weight="medium" variant="muted">
          {label}
        </Text>
        <Tooltip>
          <TooltipTrigger render={<Box direction="row" align="center" />}>
            <Icon name="help-circle" size="sm" color="faint" />
          </TooltipTrigger>
          <TooltipPopup side="top">{STAGE_TYPE_HELP[type]}</TooltipPopup>
        </Tooltip>
      </Box>
      {onAddStage ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                aria-label={`Add ${label} stage`}
                variant="ghost"
                size="icon-xs"
                onClick={() => onAddStage(type)}
              />
            }
          >
            <Icon name="plus" size="lg" color="muted" />
          </TooltipTrigger>
          {/* The category is the tooltip's payload: four identical plus
              glyphs differ only by which band the new stage lands in. */}
          <TooltipPopup side="top">Add {label} stage</TooltipPopup>
        </Tooltip>
      ) : null}
    </Box>
  );
}

// The persistent dashed add row under each category's stages — the same
// affordance as the header's ＋, spelled out where the new row will appear.
// The louder `border-strong` hairline keeps the dash legible where the
// default border reads as noise (className = tracked library gap; Box's
// borderColor axis has no strong member yet).
function AddStageRow({
  type,
  onAddStage,
}: {
  type: StageType;
  onAddStage: (type: StageType) => void;
}) {
  const label = stageTypeLabel(type);
  return (
    <Box
      as="button"
      // Starts with the visible "Add stage", and differs from the header's ＋.
      aria-label={`Add stage to ${label}`}
      direction="row"
      align="center"
      gap="sm"
      border
      rounded="lg"
      borderStyle="dashed"
      minHeight={32}
      paddingX="xs"
      cursor="pointer"
      hoverBackground
      className="border-border-strong"
      onClick={() => onAddStage(type)}
    >
      {/* Mirrors the pill rows' 16px grip lane so the ＋ sits on the same
          column as the stage glyphs above it. */}
      <Box width={16} shrink={false} />
      <Icon name="plus" size="md" color="faint" />
      <Text size="sm" variant="muted">
        Add stage
      </Text>
    </Box>
  );
}

export function DraftGroupSection({
  type,
  drafts,
  groupsRef,
  editingKey,
  duplicateKeys,
  actions,
  onAddStage,
}: {
  type: StageType;
  drafts: StageDraft[];
  groupsRef: DraftGroups;
  editingKey: string | null;
  duplicateKeys: Set<string>;
  actions: DraftActions;
  onAddStage: (type: StageType) => void;
}) {
  const keys = useMemo(() => drafts.map((d) => d.key), [drafts]);

  return (
    // Named by its category, so a screen reader hears which band a stage is in.
    <Box aria-label={stageTypeLabel(type) ?? undefined} gap="sm" role="group">
      <GroupHeader type={type} onAddStage={onAddStage} />
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        {drafts.map((draft) => (
          <SortableDraftRow
            key={draft.key}
            draft={draft}
            editing={draft.key === editingKey}
            duplicate={duplicateKeys.has(draft.key)}
            deleteReason={deleteBlocker(draft, groupsRef)}
            actions={actions}
          />
        ))}
      </SortableContext>
      <AddStageRow type={type} onAddStage={onAddStage} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

// The chip glyph, standing alone in the pill's glyph slot. The pill is an
// editable CONTROL (the label swaps to an input in place), so — like the
// board headers' StageInlineEditor — visual and label render as siblings
// here rather than through one chip: the label must become an input without
// anything around it shifting.
function DraftGlyph({ draft }: { draft: StageDraft }) {
  return (
    <EntityTag
      entity={{
        kind: "stage",
        id: draft.key,
        stage: {
          name: draft.name,
          type: draft.type,
          icon: {
            color: draft.color,
            percentage: STAGE_TYPE_PERCENTAGE[draft.type],
          },
        },
      }}
      display="visual"
      // "xs" draws the pie at the 14px the edit-mode icon-xs Button pins
      // un-classed SVGs to — one size in both states, so entering edit never
      // resizes the glyph.
      size="xs"
      interactive={false}
    />
  );
}

// The glyph's 24px footprint, shared by both states: at rest a plain slot,
// in edit the same-size icon-xs Button — so the glyph and everything after
// it hold their x through the swap.
function DraftGlyphSlot({ draft }: { draft: StageDraft }) {
  return (
    <Box width={24} shrink={false} align="center" justify="center">
      <DraftGlyph draft={draft} />
    </Box>
  );
}

/** A stage's name for a screen reader; a blank draft is still a stage. */
function stageDisplayName(draft: StageDraft): string {
  return draft.name.trim() || "Unnamed stage";
}

// The resting label + meta. A blank name (cleared, then committed) keeps the
// row and reads as its own placeholder — the error line below says why.
function DraftLabel({ draft }: { draft: StageDraft }) {
  const blank = draft.name.trim() === "";
  return (
    <>
      {blank ? (
        <Text size="sm" variant="faint">
          Stage name
        </Text>
      ) : (
        <Text size="sm" truncate>
          {draft.name}
        </Text>
      )}
      <Box direction="row" gap="xs" align="center" shrink={false}>
        {draft.id === null ? (
          <Text size="sm" variant="muted">· New</Text>
        ) : draft.issueCount > 0 ? (
          // The bare count, spelled out on hover — "Projects" repeated on
          // every row was noise.
          <Tooltip>
            <TooltipTrigger render={<Box direction="row" align="center" />}>
              <Text size="sm" variant="muted">· {draft.issueCount.toLocaleString()}</Text>
            </TooltipTrigger>
            <TooltipPopup side="top">
              {draft.issueCount === 1
                ? "1 Project in this stage"
                : `${draft.issueCount.toLocaleString()} Projects in this stage`}
            </TooltipPopup>
          </Tooltip>
        ) : null}
        {draft.isDefault ? (
          // The one surface a SystemMarker chip cannot ride: this pill is an
          // editable control whose label swaps to an input in place, and its
          // meta reads as a dot-joined run. It states the same fact, so the
          // hint comes from the same copy — and what "default" MEANS lives
          // here, on the marker itself, so the category tooltip stays about
          // the category.
          <Tooltip>
            <TooltipTrigger render={<Box direction="row" align="center" />}>
              <Text size="sm" variant="muted">· Default</Text>
            </TooltipTrigger>
            <TooltipPopup side="top">{defaultMarkerHint("stage")}</TooltipPopup>
          </Tooltip>
        ) : null}
      </Box>
    </>
  );
}

function DraftActionsMenu({
  draft,
  deleteReason,
  actions,
  onChangeColor,
  onOpenChange,
}: Omittable<{
  draft: StageDraft;
  deleteReason: string | null;
  actions: DraftActions;
  /** Enter edit with the color palette already open. */
  onChangeColor: () => void;
  onOpenChange?: (open: boolean) => void;
}>) {
  return (
    <Menu onOpenChange={onOpenChange}>
      {/* MenuTrigger's prop set is closed, so the TooltipTrigger sits INSIDE
          its render chain — the tooltip's hover handlers merge down onto the
          button element itself. */}
      <Tooltip>
        <MenuTrigger
          render={
            <TooltipTrigger
              render={
                <Button
                  // Every row has this button; the name says whose it is.
                  aria-label={`Stage actions for ${stageDisplayName(draft)}`}
                  variant="ghost"
                  size="icon-xs"
                />
              }
            />
          }
        >
          {/* The DataTable rows' kebab, exactly — horizontal, default size. */}
          <Icon name="ellipsis" />
        </MenuTrigger>
        <TooltipPopup side="top">Stage actions</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end" width="action">
        {/* A disabled row says why in its tooltip (`disabledReason`). */}
        {draft.isDefault ? (
          <MenuItem disabled disabledReason="Already the default stage">
            <Icon name="play-circle" color="muted" />
            Set as default
          </MenuItem>
        ) : (
          <MenuItem onClick={() => actions.onSetDefault(draft.key)}>
            <Icon name="play-circle" color="muted" />
            Set as default
          </MenuItem>
        )}
        <MenuItem onClick={onChangeColor}>
          <Icon name="palette" color="muted" />
          Change color
        </MenuItem>
        {/* The blocker, spelled out on hover — mirrors the floors the server
            enforces on Apply. */}
        {deleteReason !== null ? (
          <MenuItem disabled disabledReason={deleteReason}>
            <Icon name="trash" color="muted" />
            Delete stage
          </MenuItem>
        ) : (
          <MenuItem onClick={() => actions.onDelete(draft.key)}>
            <Icon name="trash" color="muted" />
            Delete stage
          </MenuItem>
        )}
      </MenuPopup>
    </Menu>
  );
}

function SortableDraftRow({
  draft,
  editing,
  duplicate,
  deleteReason,
  actions,
}: {
  draft: StageDraft;
  editing: boolean;
  duplicate: boolean;
  deleteReason: string | null;
  actions: DraftActions;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  // Set from the ⋯ menu; opened once the edit row (the popover's anchor) has
  // rendered, so the palette never positions against a missing anchor.
  const [colorPending, setColorPending] = useState(false);
  useEffect(() => {
    if (!colorPending || !editing) return;
    setColorPending(false);
    setColorOpen(true);
  }, [colorPending, editing]);
  // Pressing the swatch (and working inside its popover) blurs the input,
  // and blur exits edit mode — the palette holds the row in edit while open.
  const holdEditRef = useRef(false);
  // Escape's restore point, captured when the row enters edit.
  const preEditNameRef = useRef(draft.name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // "Edit" in the row menu opens the input, then the closing menu hands
  // focus back to its trigger. That blur is the menu's, not the user's.
  const reclaimFromMenuSteal = useMenuFocusStealGuard(editing);
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: draft.key,
  });

  const blank = draft.name.trim() === "";

  const enterEdit = useCallback(() => {
    preEditNameRef.current = draft.name;
    actions.onEdit(draft.key);
  }, [draft.name, draft.key, actions]);

  const exitEdit = useCallback(() => {
    holdEditRef.current = false;
    setColorOpen(false);
    // A NEW row left unnamed on the way out (blur, Enter) never existed —
    // drop it. An existing row cleared blank stays, with its error, so a
    // slip can't silently revert a rename.
    if (draft.id === null && draft.name.trim() === "") {
      actions.onDelete(draft.key);
    }
    actions.onExitEdit();
  }, [actions, draft.id, draft.name, draft.key]);

  // Escape restores the name the edit started from; a new row that never
  // held a name is cancelled outright.
  const cancelEdit = useCallback(() => {
    const previous = preEditNameRef.current;
    if (previous.trim() === "" && draft.id === null) {
      actions.onDelete(draft.key);
      actions.onExitEdit();
      return;
    }
    actions.onRename(draft.key, previous);
    exitEdit();
  }, [draft.id, draft.key, actions, exitEdit]);

  const handleColorOpenChange = useCallback(
    (open: boolean) => {
      setColorOpen(open);
      holdEditRef.current = open;
      // Palette closed — hand focus back so blur keeps meaning "leave edit".
      if (!open) inputRef.current?.focus();
    },
    [],
  );

  return (
    <Box
      ref={setNodeRef}
      style={sortableStyle(transform, { transition, isSorting, isDragging, dragOpacity: 0.3 })}
    >
      <Box gap="xs">
        {/* ONE pill for both states — entering edit swaps only the glyph
            (chip → recolor swatch) and the label (text → bare input); the
            grip, the geometry and the ⋯ hold still, so nothing shifts. */}
        <Box
          aria-label={stageDisplayName(draft)}
          group="sortable-row"
          role="group"
          direction="row"
          align="center"
          gap="xs"
          border
          borderColor={editing ? "primary" : undefined}
          rounded="lg"
          background="background"
          minHeight={32}
          paddingX="xs"
          hoverBackground={!editing}
        >
          {/* The 16px grip lane is the ONLY drag handle. It stays live while
              editing — grabbing it blurs the input (exiting edit with the
              typed name kept) and the drag proceeds, ClickUp's seamless
              edit-to-drag. Hover-revealed at rest (standing on a coarse
              pointer, which has no hover), visible in edit. */}
          <Box
            width={16}
            shrink={false}
            align="center"
            justify="center"
            hoverReveal={!editing ? "sortable-row" : undefined}
            cursor={isDragging ? "grabbing" : "grab"}
            {...DND_DRAGGABLE_PROPS}
            {...sortableHandleProps(attributes, listeners)}
            // No keyboard sensor drives it, so it takes no tab stop and
            // stays out of the accessibility tree.
            aria-hidden
            tabIndex={-1}
          >
            <Icon name="grip-vertical" size="md" color="muted" />
          </Box>

          {editing ? (
            <>
              {/* The stage glyph holds its slot in edit too — it just gains
                  the recolor popup (the library's one color picker, a bottom
                  sheet on a phone), and repaints live as picks apply.
                  Controlled: the row menu's "Change color" opens it. */}
              <VisualPicker
                color={draft.color}
                onColorChange={(color) => actions.onRecolor(draft.key, color)}
                onOpenChange={handleColorOpenChange}
                open={colorOpen}
                tooltip="Change color"
                trigger={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    shape="square"
                    aria-label="Stage color"
                    // Button's base dims descendant SVGs to 80% —
                    // fine for control icons, but this svg is the
                    // stage's IDENTITY glyph and must not soften
                    // between states. Same variant string so
                    // tailwind-merge replaces the base rule.
                    // (className = tracked library gap.)
                    className="[&_svg:not([class*='opacity-'])]:opacity-100"
                    onPointerDown={() => {
                      holdEditRef.current = true;
                    }}
                  >
                    <DraftGlyph draft={draft} />
                  </Button>
                }
              />
              {/* Live-bound: the input IS the draft name, so duplicate
                  errors track keystrokes and blur has nothing to commit. */}
              <Box flex={1} shrink minWidth={0} direction="column" justify="center">
                <Input
                  ref={inputRef}
                  aria-label="Stage name"
                  autoFocus
                  bare
                  // `bare` sheds the shell's type ramp with everything else,
                  // so the raw input falls to the UA's 16px — restate the
                  // resting label's 14px. (className = tracked library gap.)
                  className="text-sm"
                  value={draft.name}
                  placeholder="Stage name"
                  onChange={(event) =>
                    actions.onRename(draft.key, event.currentTarget.value)
                  }
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={(event) => {
                    if (reclaimFromMenuSteal(event)) return;
                    if (holdEditRef.current) return;
                    exitEdit();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") exitEdit();
                    if (event.key === "Escape") {
                      // Escape cancels THIS edit only — stop it before the
                      // dialog's own dismiss sees it and closes the window.
                      event.stopPropagation();
                      cancelEdit();
                    }
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              flex={1}
              shrink
              minWidth={0}
              direction="row"
              align="center"
              gap="xs"
              // A press on the name starts its rename. The role, the name and
              // the keys let a keyboard or a screen reader start it too.
              role="button"
              tabIndex={0}
              aria-label={`Rename ${stageDisplayName(draft)}`}
              onPointerUp={(e) => {
                if ((e as any).button !== 0) return;
                enterEdit();
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                enterEdit();
              }}
            >
              <DraftGlyphSlot draft={draft} />
              <DraftLabel draft={draft} />
            </Box>
          )}

          {/* ⋯ holds its place in both states; `menuOpen` pins it so it
              cannot vanish under its own open popup. */}
          <Box
            shrink={false}
            align="center"
            justify="center"
            hoverReveal={!editing && !menuOpen ? "sortable-row" : undefined}
          >
            <DraftActionsMenu
              draft={draft}
              deleteReason={deleteReason}
              actions={actions}
              // Enter edit with the palette already up: the popover holds
              // the row in edit (holdEditRef) exactly as a swatch press does.
              onChangeColor={() => {
                preEditNameRef.current = draft.name;
                holdEditRef.current = true;
                setColorPending(true);
                actions.onEdit(draft.key);
              }}
              onOpenChange={setMenuOpen}
            />
          </Box>
        </Box>

        {blank && !editing ? (
          <Box paddingX="sm">
            <Text size="xs" variant="destructive">
              Stage name cannot be blank
            </Text>
          </Box>
        ) : duplicate ? (
          <Box paddingX="sm">
            <Text size="xs" variant="destructive">
              Stage name is already taken
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

// Drag overlay content — the pill, lifted.
export function DraftRowCard({ draft }: { draft: StageDraft }) {
  return (
    <Box
      direction="row"
      align="center"
      gap="xs"
      border
      rounded="lg"
      background="background"
      minHeight={32}
      paddingX="xs"
      shadow="md"
    >
      <Box width={16} shrink={false} align="center" justify="center">
        <Icon name="grip-vertical" size="md" color="muted" />
      </Box>
      <Box direction="row" align="center" gap="xs" shrink>
        <DraftGlyphSlot draft={draft} />
        <DraftLabel draft={draft} />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Loading mirror — real category headers, anonymous pill bars
// ---------------------------------------------------------------------------

export function StageGroupSkeleton({ type }: { type: StageType }) {
  return (
    <Box gap="sm">
      <GroupHeader type={type} />
      <Box
        direction="row"
        align="center"
        border
        rounded="lg"
        minHeight={32}
        paddingX="sm"
      >
        <Skeleton delayed height={12} width={140} />
      </Box>
    </Box>
  );
}
